import React, {useState} from 'react';
import {get, map, forEach, uniq} from 'lodash';
import className from 'classnames';
import moment from 'moment';
import axios from 'axios';

import {
  CSRF_TOKEN,
  requestCallback,
} from '../lib/helpers';

import {
  canManage,
} from '../lib/policies';

import {
  useModal,
  useCheckIds,
} from '../hooks';

import Modal from './Modal';
import InputPassword from './InputPassword';
import ModalDelete from './ModalDelete';

const AdminsList = ({
  admins = [],
  total = 0,
  apps = ['marinchat'],
  features = ['Post', 'Message', 'Group'],
  permissions = ['Read', 'Kenta', 'Ban', 'Edit', 'Manage'],
}) => {
  apps = [
    'all',
    ...apps,
  ]

  const {
    checkIds,
    renderCheckBox, renderCheckBoxForCheckAll,
  } = useCheckIds({
    data: admins,
    openUrl: 'admins',
  })

  const [
    modal,
    modalSet,
    $submitButton,
  ] = useModal({
    keyboardBindings: true,
  })

  const [
    modalEditAdmin,
    modalEditAdminSet,
    $submitButtonEditAdmin,
  ] = useModal({
    keyboardBindings: true,
  })

  const [
    modalEditAdminPermission,
    modalEditAdminPermissionSet,
    $submitButtonEditAdminPermission,
  ] = useModal({
    keyboardBindings: true,
  })

  const [checkPermissions, setCheckPermissions] = useState([])

  function onToggleEditAdminPermissionModal(admin) {
    return () => {
      let _checkPermissions = []

      forEach(get(admin, 'permissions'), (x) => {
        const app = get(x, 'key')
        const features = get(x, 'permissions[0].items')
        const permissions = map(get(x, 'permissions'), (y) => y.key)

        _checkPermissions = [
          ..._checkPermissions,
          `${app}.all`,
          ...map(features, (x) => `${app}.${x}`),
          ...map(permissions, (x) => `${app}.${x}`),
        ]
      })

      setCheckPermissions(_checkPermissions)
      modalEditAdminPermissionSet(!admin ? null : admin)
    }
  }

  function onInput(app) {
    return (e) => {
      const $el = e.target
      const [args1, args2] = $el.name.split('.')

      if (!checkPermissions.find((x) => x == $el.name)) {
        let toBeAdded = [$el.name]

        if (args1 == 'all') {
          toBeAdded = [
            ...toBeAdded,
            ...apps.map((x) => `${x}.${args2}`),
            ...apps.filter((x) => x != 'all').map((x) => `${x}.all`)
          ]

          if (args2 == 'Ban') {
            toBeAdded = [
              ...toBeAdded,
              ...apps.map((x) => `${x}.Kenta`),
            ]
          }

          if (args2 == 'Edit') {
            toBeAdded = [
              ...toBeAdded,
              ...apps.map((x) => `${x}.Kenta`),
              ...apps.map((x) => `${x}.Ban`),
            ]
          }

          if (args2 == 'Manage') {
            toBeAdded = [
              ...toBeAdded,
              ...apps.map((x) => `${x}.Kenta`),
              ...apps.map((x) => `${x}.Ban`),
              ...apps.map((x) => `${x}.Edit`),
            ]
          }

          if (args2 == 'all') {
            toBeAdded = [
              ...toBeAdded,
              ...apps.map((x) => `${x}.Post`),
              ...apps.map((x) => `${x}.Message`),
              ...apps.map((x) => `${x}.Group`),
              ...apps.map((x) => `${x}.Kenta`),
              ...apps.map((x) => `${x}.Ban`),
              ...apps.map((x) => `${x}.Edit`),
              ...apps.map((x) => `${x}.Manage`),
            ]
          }
        }
        else {
          if (!checkPermissions.find((x) => x == `${app}.all`)) {
            toBeAdded = [...toBeAdded, `${app}.all`]
          }

          if (args2 == 'Ban') {
            toBeAdded = [
              ...toBeAdded,
              `${app}.Kenta`,
            ]
          }

          if (args2 == 'Edit') {
            toBeAdded = [
              ...toBeAdded,
              `${app}.Kenta`,
              `${app}.Ban`,
            ]
          }

          if (args2 == 'Manage') {
            toBeAdded = [
              ...toBeAdded,
              `${app}.Kenta`,
              `${app}.Ban`,
              `${app}.Edit`,
            ]
          }
        }

        setCheckPermissions((prevState) => (uniq([
          ...prevState,
          ...toBeAdded,
        ])))
      }
      else {
        let toBeRemoved = [$el.name]

        if (args1 == 'all') {
          if (args2 == 'all') {
            toBeRemoved = [
              ...toBeRemoved,
              ...apps.map((x) => `${x}.Post`),
              ...apps.map((x) => `${x}.Message`),
              ...apps.map((x) => `${x}.Group`),
              ...apps.map((x) => `${x}.Kenta`),
              ...apps.map((x) => `${x}.Ban`),
              ...apps.map((x) => `${x}.Edit`),
              ...apps.map((x) => `${x}.Manage`),
              ...apps.map((x) => `${x}.all`),
            ]
          }
        }

        setCheckPermissions((prevState) => (
          prevState.filter((x) => !toBeRemoved.includes(x))
        ))
      }
    }
  }

  /* Data structure
    [
      {
        key: 'himabu',
        permissions: [
          {
            key: 'Read',
            items: ['Post', 'Group', 'Message']
          },
          {
            key: 'Ban',
            items: ['Post', 'Group', 'Message']
          },
          ...
        ]
      },
      {
        key: 'marinchat',
        ...
      }
    ]
  */

  function _formatDataForServer(e) {
    const data = map(e.target.elements, (x) => {
      return {
        label: x.name,
        checked: x.checked,
      }
    })
      .filter((x) => x.label && x.checked)

    const result = apps
      .filter((app) => {
        return (
          app != 'all' &&
          data.find((d) => d.label.startsWith(`${app}.all`))
        )
      })
      .map((app) => {
        return {
          key: app,
          permissions: permissions
            .filter((permission) => {
              return data.find((d) => d.label == `${app}.${permission}`)
            })
            .map((permission) => {
              return {
                key: permission,
                items: [
                  ...features.filter((feature) => {
                    return data.find((d) => d.label == `${app}.${feature}`)
                  }),
                  'User',
                ]
              }
            })
        }
      })
      .filter((app) => get(app, 'permissions.length'))

    return result
  }

  function onSubmitAddAdmin(e) {
    e.preventDefault()
    const $els = e.target.elements
    const result = _formatDataForServer(e)

    requestCallback(axios.post(`/${gon.currentProvider}/api/admins`, {
      admin: {
        email: $els.email.value,
        password: $els.password.value,
        permissions: result,
      },
      authenticity_token: CSRF_TOKEN,
    }))
  }

  function onSubmitEditAdminManagement(e) {
    e.preventDefault()
    const result = _formatDataForServer(e)
    const id = get(modalEditAdminPermission, 'id')

    requestCallback(axios.put(`/${gon.currentProvider}/api/admins/${id}/update_permissions`, {
      admin: {
        permissions: result,
      },
      authenticity_token: CSRF_TOKEN,
    }))
  }

  function onSubmitEditAdmin(e) {
    e.preventDefault()
    const $els = e.target.elements
    const id = get(modalEditAdmin, 'id')

    requestCallback(axios.put(`/${gon.currentProvider}/api/admins/${id}`, {
      admin: {
        email: $els.email.value,
        password: $els.password.value,
        password_confirmation: $els.passwordConfirm.value,
        active: $els.active.value,
      },
      authenticity_token: CSRF_TOKEN,
    }))
  }

  function renderCheckBoxAdmin(x, name) {
    return (
      <input
        type='checkbox'
        name={name}
        checked={(() => {
          if (name.endsWith('.Read')) {
            return true
          }

          return checkPermissions.find((y) => y == name)
        })() || false}
        onInput={onInput(x)}
        onChange={() => {}}
      />
    )
  }

  function renderAdminModalTable() {
    return (
      <div className='AddAdminModal__table'>
        {(() => {
          let data = [
            {label: 'App', rightBordered: true},
            {label: 'Post'},
            {label: 'Message'},
            {label: 'Group', rightBordered: true},
          ]

          permissions.forEach((permission) => (
            data = [...data, {label: permission}]
          ))

          return (
            <div className='AddAdminModal__table__row'>
              {data.map((x, i) => (
                <div
                  key={i}
                  className={className('AddAdminModal__table__col', {
                    'AddAdminModal__table__col--th': true,
                    'AddAdminModal__table__col--bdr': x.rightBordered,
                  })}
                >
                  {x.label}
                </div>
              ))}
            </div>
          )
        })()}

        {apps.map((x, i) => (
          <div
            key={i}
            className='AddAdminModal__table__row'
          >
            <div className='AddAdminModal__table__col AddAdminModal__table__col--bdr'>
              <label
                className='AddAdminModal__table__label'
              >
                {renderCheckBoxAdmin(x, `${x}.all`)}
                <span className='CheckBox' />
                <span className='AddAdminModal__table__span'>{x}</span>
              </label>
            </div>

            {features.map((y, j) => (
              <div
                key={j}
                className={className('AddAdminModal__table__col', {
                  'AddAdminModal__table__col--bdr': j == 2,
                })}
              >
                <label
                  className='AddAdminModal__table__label'
                >
                  {renderCheckBoxAdmin(x, `${x}.${y}`)}
                  <span className='CheckBox' />
                </label>
              </div>
            ))}

            {permissions.map((y, j) => (
              <div
                key={j}
                className={className('AddAdminModal__table__col', {
                })}
              >
                <label
                  className='AddAdminModal__table__label'
                  disabled={(() => {
                    switch (y) {
                      case 'Read': return true
                      case 'Kenta': return checkPermissions.includes(`${x}.Ban`)
                      case 'Ban': return checkPermissions.includes(`${x}.Edit`)
                      case 'Edit': return checkPermissions.includes(`${x}.Manage`)
                      default: return false
                    }
                  })()}
                >
                  {y !== '' && renderCheckBoxAdmin(x, `${x}.${y}`)}
                  {y !== '' && <span className='CheckBox' />}
                </label>
              </div>
            ))}
          </div>
        ))}
      </div>
    )
  }

  return (
    <React.Fragment>
      <div className='MainHeader'>
        <div className='MainHeader__left'>
          <h2 className='MainHeader__h2'>Admin users list</h2>
          <p className='MainHeader__left__p'>Total {total.toLocaleString()} admin users</p>
        </div>

        {canManage() &&
          <div className='MainHeader__right'>
            <a
              className='MainHeader__right__a MainHeader__right__a--bright-blue'
              onClick={() => {
                modalSet(true)
              }}
            >
              Add new admin
            </a>
          </div>
        }
      </div>

      <div className='TableChecker'>
        {renderCheckBoxForCheckAll()}

        {canManage() &&
          <div className='TableChecker__right'>
            <a className='TableChecker__right__a TableChecker__right__a--person' />
            <a className='TableChecker__right__a TableChecker__right__a--trash' />
          </div>
        }
      </div>

      <div className='Table Table--admins-list'>
        {(() => {
          const columnHeaders = [
            '', 'ID', 'Email', 'Report Type', 'Role', 'App',
            'Status', 'Updated at','Manage',
          ]

          return (
            <div className='Table__row'>
              {columnHeaders.map((header, key) => (
                <div
                  key={key}
                  className='Table__col Table__col--th'
                >
                  {header}
                </div>
              ))}
            </div>
          )
        })()}

        {admins.map((admin, i) => (
          <div
            className={className('Table__row', {
              'Table__row--active': checkIds.includes(admin.id),
            })}
            key={i}
            data-x={admin}
          >
            <div className='Table__col'>
              {renderCheckBox(admin)}
            </div>
            <div className='Table__col'>{get(admin, 'id')}</div>
            <div className='Table__col'>{get(admin, 'email')}</div>
            <div className='Table__col'>{get(admin, 'reportType')}</div>
            <div className='Table__col'>{get(admin, 'role')}</div>
            <div className='Table__col'>{get(admin, 'app')}</div>
            <div className='Table__col'>
              {get(admin, 'active') ?
                <span className='Badge'>ACTIVE</span>
              :
                <span className='Badge Badge--orange'>INACTIVE</span>
              }
            </div>
            <div
              className='Table__col'
              dangerouslySetInnerHTML={
                {
                  __html: admin.updatedAt !== null ?
                    moment(admin.updatedAt).format('YYYY-MM-DD<br />HH:mm')
                  :
                    'N/A'
                }
              }
            />
            <div className='Table__col'>
              {!canManage() && gon.currentAdmin.id === get(admin, 'id') &&
                <div className='Table__controls'>
                  <a
                    className='Table__controls__a Table__controls__a--pencil'
                    onClick={() => {
                      modalEditAdminSet(admin)
                    }}
                  />
                </div>
              }

              {canManage() &&
                <div className='Table__controls'>
                  <a
                    className='Table__controls__a Table__controls__a--pencil'
                    onClick={() => {
                      modalEditAdminSet(admin)
                    }}
                  />

                  {gon.currentAdmin.id !== get(admin, 'id') &&
                    <React.Fragment>
                      <a
                        className='Table__controls__a Table__controls__a--person'
                        onClick={onToggleEditAdminPermissionModal(admin)}
                      />

                      <ModalDelete
                        type='admin'
                        id={get(admin, 'id')}
                        klassName='Table__controls__a Table__controls__a--trash'
                        reload
                        hideText
                      />
                    </React.Fragment>
                  }
                </div>
              }
            </div>
          </div>
        ))}
      </div>

      <Modal
        isActive={modal}
        onToggleModal={() => {
          modalSet(false)
        }}
      >
        <form
          onSubmit={onSubmitAddAdmin}
        >
          <h3 className='Modal__h3'>Add new admin</h3>

          <div className='AddAdminModal'>
            <input
              type='email'
              className='Input'
              placeholder='Email'
              name='email'
              required
            />

            <InputPassword
              required
            />

            {renderAdminModalTable()}
          </div>

          <div className='Modal__footer Modal__footer--without-border'>
            <a
              className='Button Button--gray-light'
              onClick={() => {
                modalSet(false)
              }}
            >
              Cancel
            </a>

            <button
              className='Button Button--blue'
              ref={$submitButton}
            >
              Submit
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isActive={modalEditAdmin}
        onToggleModal={() => modalEditAdminSet(false)}
      >
        <form
          onSubmit={onSubmitEditAdmin}
        >
          <h3 className='Modal__h3'>Edit admin</h3>

          <div className='EditAdminModal'>
            <input
              type='email'
              className='Input'
              placeholder='Email'
              name='email'
              value={get(modalEditAdmin, 'email') || ''}
              onChange={(e) => {
                const $el = e.target

                modalEditAdminSet({
                  ...modalEditAdmin,
                  email: $el.value,
                })
              }}
            />

            <InputPassword
            />

            <InputPassword
              name='passwordConfirm'
              placeholder='Change Password (leave black if don’t change)'
            />

            <select
              className='Select'
              name='active'
              value={get(modalEditAdmin, 'active')}
              onChange={(e) => {
                const $el = e.target

                modalEditAdminSet({
                  ...modalEditAdmin,
                  active: $el.value,
                })
              }}
            >
              <option value={true}>Active</option>
              <option value={false}>Inactive</option>
            </select>
          </div>

          <div className='Modal__footer Modal__footer--without-border'>
            <a
              className='Button Button--gray-light'
              onClick={() => modalEditAdminSet(false)}
            >
              Cancel
            </a>

            <button
              className='Button Button--blue'
              ref={$submitButtonEditAdmin}
            >
              Submit
            </button>
          </div>
        </form>
      </Modal>


      <Modal
        isActive={modalEditAdminPermission}
        onToggleModal={onToggleEditAdminPermissionModal(null)}
      >
        <form
          onSubmit={onSubmitEditAdminManagement}
        >
          <h3 className='Modal__h3'>Edit management</h3>

          <div className='AddAdminModal'>
            {renderAdminModalTable()}
          </div>

          <div className='Modal__footer Modal__footer--without-border'>
            <a
              className='Button Button--gray-light'
              onClick={onToggleEditAdminPermissionModal(null)}
            >
              Cancel
            </a>

            <button
              className='Button Button--blue'
              ref={$submitButtonEditAdminPermission}
            >
              Submit
            </button>
          </div>
        </form>
      </Modal>
    </React.Fragment>
  );
}

export default AdminsList;
